import { useForm } from "react-hook-form";
import { classes } from "../../../../util/style-utils";
import useYesNoOptions from "../../../../hooks/useYesNoOptions";
import { v4 } from 'uuid';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import "@gdk/base/dist/js/jquery.maskedinput.geico";
import FileUploadForm from '../../../FileUpload/FileUploadForm';
import { FileUploadState } from '../../../FileUpload/FileUploadState';
import { GdkDatePicker } from "@gdk/datepicker";
import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GeaoContext } from "../../GeaoContextProvider";
import { update, uploadOnboardingErrorsAndOmissions } from "../../../../api/onboarding";
import GeaoVerticalStepperLayout from "../shared/Layout/GeaoVerticalStepperLayout";
import PrincipalFlowVerticalStepperElements from "../../constants/PrincipalFlowVerticalStepperElements";
import $ from 'jquery';
import { GeaoPaths } from "../../constants/GeaoPaths";
import useTitle from "../../../../hooks/useTitle";
import { InputNumberFormat } from '@react-input/number-format';
import { PageStatus } from "../../hooks/useInterviewUpdater";

const currencyReg = /^(([1-9]\d*)|(\$[1-9]\d{0,2}(?:,\d{3})*))$/

const validationSchema = yup.object().shape({
    carrier: yup.string().required("Please enter your carrier"),
    policyNumber: yup.string().typeError("Invalid Entry.\nPlease check your entry and try again.").required("Please enter your policy number"),
    policyEffectiveDate: yup.date().required().typeError("Invalid date format.\nPlease check your entry and try again."),
    policyExpirationDate: yup.date().required().typeError("Invalid date format.\nPlease check your entry and try again."),
    perClaimAmount: yup.string().matches(currencyReg, "Invalid Entry.\nPlease check your entry and try again.").required("Please check your entry and try again."),
    aggregateClaimAmount: yup.string().matches(currencyReg, "Invalid Entry.\nPlease check your entry and try again.").required("Please check your entry and try again."),
    underSameEoPolicy: yup.bool().required("Please make a selection."),
    fileUploadState: yup.string().oneOf([FileUploadState.Complete], "Please upload a current copy of your E&O policy declarations page.").required("Please upload a current copy of your E&O policy declarations page.")
});

export default function PrincipalErrorsAndOmissions() {
    useTitle("Errors and Omissions");

    const { agencyInterview, setAgencyInterview, agentInterview, interviewIsCompleted } = useContext(GeaoContext);
    const navigate = useNavigate();
    const { register, handleSubmit, setValue, clearErrors, formState: { errors }, getValues, trigger } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            carrier: agencyInterview?.questionAnswers?.EoCarrierName?.answer,
            policyNumber: agencyInterview?.questionAnswers?.EoPolicyNumber?.answer,
            policyEffectiveDate: agencyInterview?.questionAnswers?.EoPolicyEffectiveDate?.answer,
            policyExpirationDate: agencyInterview?.questionAnswers?.EoPolicyExpirationDate?.answer,
            perClaimAmount: agencyInterview?.questionAnswers?.PerClaimAmount?.answer,
            aggregateClaimAmount: agencyInterview?.questionAnswers?.AggregateClaimAmount?.answer,
            underSameEoPolicy: agencyInterview?.questionAnswers?.CoveredUnderSameEoPolicy?.answer
        }
    });

    const [buttonsDisabled, setButtonsDisabled] = useState(false);
    const [fileUploadState, setFileUploadState] = useState(FileUploadState.Upload);

    const yesNoOptions = useYesNoOptions();

    const effectiveDatePickerRef = useRef();

    useEffect(() => {
        effectiveDatePickerRef.current = new GdkDatePicker({
            content: "#policy-effective-date",
            monthYearDropdowns: true,
            dateSelected: (date) => {
                clearErrors("policyEffectiveDate");
                setValue("policyEffectiveDate", date);
            },
        });

        $("#policy-effective-date").mask("99/99/9999", {});
    }, []);

    const expirationDatePickerRef = useRef();

    useEffect(() => {
        expirationDatePickerRef.current = new GdkDatePicker({
            content: "#policy-expiration-date",
            monthYearDropdowns: true,
            dateSelected: (date) => {
                clearErrors("policyExpirationDate");
                setValue("policyExpirationDate", date);
            },
        });

        $("#policy-expiration-date").mask("99/99/9999", {});
    }, []);

    useEffect(() => {
        setFileUploadState(agencyInterview?.questionAnswers?.EoCarrierName?.answer ? FileUploadState.Complete : FileUploadState.Upload);
    }, [])

    useEffect(() => {
        setValue("fileUploadState", fileUploadState);
    }, [fileUploadState]);

    function onSubmit(values) {
        updateInterview(values, GeaoPaths.PrincipalReviewAndSign);
    };

    function onSaveAndExit() {
        trigger().then(validationPassed => {
            if (validationPassed) {
                updateInterview(getValues(), GeaoPaths.CreateYourProfileChecklist, true);
            }
        });
    }

    function updateInterview(data, nextPath, isSaveAndExit = false) {

        let activityRequest = null;
        if (isSaveAndExit) {
            activityRequest = {
                ActivityCategory: "GEAO_ONBOARDING",
                ActivitySubType: "SAVE_AND_EXIT",
                SourceApplication: "AGIWUI",
                AdditionalData: {
                    ComponentName: "PrincipalErrorsAndOmissions"
                }
            };
        }

        const updatedAgentInterview = {
            ...agentInterview,
            pageStatus: {
                ...agentInterview.pageStatus,
                "PrincipalErrorsAndOmissions": PageStatus.Completed
            }
        };

        update({
            identityType: "Agent",
            interview: updatedAgentInterview,
            activityRequest: activityRequest
        });

        const questionAnswers = {
            ...agencyInterview?.questionAnswers ?? null,
            EoCarrierName: { answer: data.carrier ?? "" },
            EoPolicyNumber: { answer: data.policyNumber ?? "" },
            EoPolicyEffectiveDate: { answer: data.policyEffectiveDate ?? "" },
            EoPolicyExpirationDate: { answer: data.policyExpirationDate ?? "" },
            PerClaimAmount: { answer: data.perClaimAmount.replace(/[,$]/g, "") ?? "" },
            AggregateClaimAmount: { answer: data.aggregateClaimAmount.replace(/[,$]/g, "") ?? "" },
            CoveredUnderSameEoPolicy: { answer: data.underSameEoPolicy.toString() ?? "" },
        }

        const updatedInterview = {
            identityType: "Agency",
            interview: {
                ...agencyInterview,
                questionAnswers: questionAnswers,
                pageStatus: {
                    ...agencyInterview.pageStatus,
                    "PrincipalErrorsAndOmissions": PageStatus.Completed
                }
            }
        }

        update(updatedInterview,
            interview => {
                setAgencyInterview(interview);
                if (interview.assessments?.ParentAgencyHasOnboarded?.status === "Rejected" ||
                    interview.assessments?.ParentAgencyHasOnboarded?.status === "InProgress") {
                    navigate(GeaoPaths.ProducerKickout);
                    return;
                }
                navigate(nextPath);
            },
            () => {
                setButtonsDisabled(false);
            });
    }

    function onFileUploadSubmit(selectedFiles) {
        uploadOnboardingErrorsAndOmissions({
            target: agencyInterview?.interviewId,
            uploadedBy: agentInterview?.interviewId,
            files: selectedFiles
        },
            () => {
                setFileUploadState(FileUploadState.Complete);
            },
            () => {
                setFileUploadState(FileUploadState.Error);
            }
        );
    }
    const fileUploadParameters = {
        allowedFileExtensions: ["jpg", "jpeg", "rtif", "tif", "tiff", "gif", "png", "mav", "xls", "xlsx", "doc", "docx", "ppt", "pptx", "pdf"],
        minFileSize: 1,
        maxFileSize: 25600,
        maxCombinedFileSize: 71680,
        maxFileCount: 1,
        combinedFileSize: 0
    };

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="E&O Policy Information"
            header="Errors & Omissions (E&O) Policy Information"
        >
            <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                <p>
                    GEICO requires that independent agents maintain Errors & Omissions
                    coverage with minimum coverage limits of $500,000 per claim and $500,000 in aggregate.
                </p>

                <div className="flex-row">
                    <div className={classes("form-field", errors.carrier && "form-field--error")}>
                        <label htmlFor="carrier" className="text">
                            E&O Carrier{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id="carrier" type="text" {...register("carrier")} />
                        {errors.carrier &&
                            <span className="form-message">{errors.carrier.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.policyNumber && "form-field--error")}>
                        <label htmlFor="policy-number" className="text">
                            Policy Number{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id="policy-number" type="text" {...register("policyNumber")} />
                        {errors.policyNumber &&
                            <span className="form-message">{errors.policyNumber.message}</span>
                        }
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.policyEffectiveDate && "form-field--error")}>
                        <label htmlFor="policy-effective-date" className="text">
                            Policy Effective Date{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="policy-effective-date"
                            className="date date-datepicker"
                            type="tel"
                            placeholder="MM/DD/YYYY"
                            {...register("policyEffectiveDate")}
                        />
                        {errors.policyEffectiveDate &&
                            <span className="form-message">{errors.policyEffectiveDate.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.policyExpirationDate && "form-field--error")}>
                        <label htmlFor="policy-expiration-date" className="text">
                            Policy Expiration Date{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="policy-expiration-date"
                            className="date date-datepicker"
                            type="tel"
                            placeholder="MM/DD/YYYY"
                            {...register("policyExpirationDate")}
                        />
                        {errors.policyExpirationDate &&
                            <span className="form-message">{errors.policyExpirationDate.message}</span>
                        }
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.perClaimAmount && "form-field--error")}>
                        <label htmlFor="per-claim-amount" className="text">
                            Per Claim Amount{/*
                            */}<span className='required'>*</span>
                        </label>
                        <InputNumberFormat
                            id="per-claim-amount"
                            locales="en-US"
                            format="currency"
                            currency="USD"
                            type="text"
                            placeholder="$"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            {...register("perClaimAmount")}
                        />
                        {errors.perClaimAmount &&
                            <span className="form-message">{errors.perClaimAmount.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.aggregateClaimAmount && "form-field--error")}>
                        <label htmlFor="aggregate-claim-amount" className="text">
                            Aggregate Claim Amount{/*
                            */}<span className='required'>*</span>
                        </label>
                        <InputNumberFormat
                            id="aggregate-claim-amount"
                            locales="en-US"
                            format="currency"
                            currency="USD"
                            type="text"
                            placeholder="$"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            {...register("aggregateClaimAmount")}
                        />
                        {errors.aggregateClaimAmount &&
                            <span className="form-message">{errors.aggregateClaimAmount.message}</span>
                        }
                    </div>
                </div>

                <fieldset role="radiogroup" className={classes("form-field radio-button-group", errors.underSameEoPolicy && "form-field--error")}>
                    <legend>
                        Are the Agency and Principal both covered under the same E&O policy?{/*
                        */}<span className='required'>*</span>
                    </legend>
                    <div className="radio-button-wrapper col-2">
                        {yesNoOptions?.map((option) => {
                            var id = `${option.label}-radio-${v4()}`;
                            return (
                                <div key={id} className="col-width-fix">
                                    <input
                                        id={id}
                                        type="radio"
                                        value={option.value}
                                        {...register("underSameEoPolicy")}
                                    />
                                    <label className="radio" htmlFor={id}>
                                        <span>{option.label}</span>
                                    </label>
                                </div>
                            );
                        })}
                        {errors.underSameEoPolicy &&
                            <span className="form-message">{errors.underSameEoPolicy.message}</span>
                        }
                    </div>
                </fieldset>

                <div className="flex-row">
                    <div className={classes("form-field w-100", errors.fileUploadState && "form-field--error")}>
                        <label className="text">
                            Upload E&O Policy Declarations Page{/*
                        */}<span className='required'>*</span>
                        </label>
                        <span className="form-descriptive-copy">Upload only one file. Max 25MB per file.</span>
                        <FileUploadForm
                            fileUploadState={fileUploadState}
                            setFileUploadState={setFileUploadState}
                            onFileUploadSubmit={onFileUploadSubmit}
                            fileUploadParameters={fileUploadParameters}
                        />
                        {errors.fileUploadState &&
                            <span className="form-message">{errors.fileUploadState.message}</span>
                        }
                    </div>
                </div>

                <div className="flex-row">
                    <p>
                        <span className='required'>*</span> Indicates required field.
                    </p>
                </div>

                <div className="margin-top">
                    <button
                        type="submit"
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                        disabled={buttonsDisabled || interviewIsCompleted}
                    >
                        <span>Next</span>
                    </button>

                    <button
                        type="button"
                        className="btn btn--secondary btn--full-mobile btn--pull-right"
                        onClick={onSaveAndExit}
                        disabled={buttonsDisabled || interviewIsCompleted}
                    >
                        <span>Save And Exit</span>
                    </button>
                </div>
            </form>
        </GeaoVerticalStepperLayout>
    );
}

